
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'cs-sidebar-collapsed';
const BREAKPOINT = 960;

function isViewportMobile() {
    return typeof window !== 'undefined' && window.innerWidth < BREAKPOINT;
}

const SidebarContext = createContext({
    collapsed: false,
    isMobile: false,
    toggleSidebar: () => {},
    setCollapsed: () => {},
});

export function SidebarProvider({ children }) {
    const [isMobile, setIsMobile] = useState(isViewportMobile);

    const [collapsed, setCollapsed] = useState(() => {
        // On mobile: always start hidden; ignore stored desktop preference
        if (isViewportMobile()) return true;
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored !== null) return JSON.parse(stored);
        } catch (_) {}
        return false;
    });

    // Persist only the desktop preference — mobile is transient
    useEffect(() => {
        if (isMobile) return;
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
        } catch (_) {}
    }, [collapsed, isMobile]);

    // Track viewport mode; auto-collapse when entering mobile
    useEffect(() => {
        function handleResize() {
            const mobile = isViewportMobile();
            setIsMobile(prev => {
                if (prev !== mobile && mobile) setCollapsed(true);
                return mobile;
            });
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSidebar = useCallback(() => setCollapsed(prev => !prev), []);

    return (
        <SidebarContext.Provider value={{ collapsed, isMobile, toggleSidebar, setCollapsed }}>
            {children}
        </SidebarContext.Provider>
    );
}

export function useSidebar() {
    return useContext(SidebarContext);
}
